import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Inicio', path: '/' },
    { label: 'Nosotros', path: '/nosotros' },
    { label: 'Servicios', path: '/servicios' },
    { label: 'Proyectos', path: '/proyectos' },
    { label: 'Instalaciones', path: '/instalaciones' },
    { label: 'Contacto', path: '/contacto' },
  ];

  const isActive = (path: string) => location.pathname === path;

  const navBg = isHome
    ? scrolled
      ? 'bg-[#0C1D2E]/95 backdrop-blur-md py-3 border-b border-white/10'
      : 'bg-transparent py-5'
    : 'bg-[#0C1D2E]/95 backdrop-blur-md py-3 border-b border-white/10';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <img
            src="https://storage.readdy-site.link/project_files/e9e9255d-4fa4-42a2-878c-87b7e92a4901/79b56ce4-7418-4545-880f-043bd4b903d4_Logo-Oficial.png?v=e4ec89a3137073739724e15345a40407"
            alt="IngemetalSur"
            className="h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 text-sm font-semibold tracking-wide transition-colors duration-200 whitespace-nowrap rounded-md ${
                isActive(link.path)
                  ? 'text-white bg-white/10'
                  : 'text-white/70 hover:text-white hover:bg-white/5'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Desktop CTA Buttons */}
        <div className="hidden md:flex items-center gap-2">
          {/* Email CTA */}
          <a
            href="mailto:contacto@ingemetalsur.cl?subject=Solicitud%20de%20Cotizaci%C3%B3n%20de%20Proyecto"
            target="_blank"
            rel="noopener noreferrer"
            className="relative inline-flex items-center gap-2 bg-gradient-to-r from-[#E6A236] to-[#C88A2A] hover:from-[#D49430] hover:to-[#B57A20] text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-all duration-200 whitespace-nowrap"
          >
            {/* Badge */}
            <span className="absolute -top-2 -right-2 bg-[#DC2626] text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-bounce">
              ¡COTIZA!
            </span>
            <i className="ri-mail-line text-base"></i>
            Cotizar Proyecto
          </a>

          {/* WhatsApp CTA */}
          <a
            href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizaci%C3%B3n"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white text-sm font-bold px-4 py-2.5 rounded-lg transition-all duration-200 whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-base"></i>
            WhatsApp
          </a>
        </div>

        {/* Hamburger */}
        <button
          className={`md:hidden w-8 h-8 flex items-center justify-center ${scrolled || !isHome ? 'text-white' : 'text-white'}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Abrir menú"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0C1D2E]/98 backdrop-blur-lg px-4 py-6 flex flex-col gap-2 border-t border-white/10">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMenuOpen(false)}
              className={`text-sm font-semibold py-3 px-4 rounded-md ${
                isActive(link.path) ? 'text-white bg-white/10' : 'text-white/70 hover:text-white hover:bg-white/5'
              }`}
            >
              {link.label}
            </Link>
          ))}

          {/* Mobile Email CTA */}
          <a
            href="mailto:contacto@ingemetalsur.cl?subject=Solicitud%20de%20Cotizaci%C3%B3n%20de%20Proyecto"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuOpen(false)}
            className="relative inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#E6A236] to-[#C88A2A] text-white text-sm font-bold px-5 py-3 rounded-lg text-center whitespace-nowrap mt-2"
          >
            <span className="absolute -top-2 right-4 bg-[#DC2626] text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-bounce">
              ¡COTIZA!
            </span>
            <i className="ri-mail-line text-base"></i>
            Cotizar Proyecto
          </a>

          {/* Mobile WhatsApp CTA */}
          <a
            href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizaci%C3%B3n"
            target="_blank"
            rel="nofollow noopener noreferrer"
            onClick={() => setMenuOpen(false)}
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white text-sm font-bold px-5 py-3 rounded-lg text-center whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-base"></i>
            WhatsApp
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;